import React from 'react';
import { Terminal, HelpCircle, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import type { SimError } from '../../simulator';
import type { CompilationIssue } from '../../utils/studentEditor';

/**
 * Console du simulateur : même style que la console d'erreurs A11 d'ExercisePage (fond sombre
 * volontaire même en light mode), mais les erreurs viennent du FAUX COMPILATEUR (déterministe),
 * pas de l'IA. Une ligne cliquable fait défiler l'éditeur jusqu'à la ligne. Pour une erreur
 * « hors périmètre » (ou de syntaxe / d'exécution), le bouton « Demander une explication »
 * déclenche la remise au LLM ; la réponse s'affiche dans l'encart « Explication du coach ».
 */

export interface ExplanationState {
  key: string;
  status: 'loading' | 'done' | 'error';
  symbol?: string;
  text?: string;
  error?: string;
}

interface SimulatorConsoleProps {
  /** `null` = jamais compilé (console masquée). */
  issues: CompilationIssue[] | null;
  errors: SimError[];
  compiledOk: boolean;
  onIssueClick: (line: number) => void;
  onExplain: (err: SimError) => void;
  explanation: ExplanationState | null;
  explainKey: (err: SimError) => string;
}

export const SimulatorConsole: React.FC<SimulatorConsoleProps> = ({
  issues, errors, compiledOk, onIssueClick, onExplain, explanation, explainKey,
}) => {
  if (issues === null) return null;

  return (
    <div className="mt-4 space-y-3" data-testid="sim-console">
      <div className="bg-gray-900 border border-gray-700 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 bg-gray-800 border-b border-gray-700">
          <div className="flex items-center space-x-2 text-gray-300 text-xs font-medium uppercase tracking-wide">
            <Terminal className="w-3.5 h-3.5" />
            <span>Console</span>
          </div>
          <span
            className="text-[11px] px-2 py-0.5 rounded-full bg-cyan-900/60 text-cyan-300 border border-cyan-800"
            title="Erreurs détectées par le compilateur pédagogique du simulateur (déterministe, sans IA)"
          >
            Compilateur du simulateur
          </span>
        </div>
        <div className="px-3 py-2 font-mono text-sm space-y-1">
          {issues.length === 0 ? (
            <div className="text-green-500/90">✓ Compilation réussie — aucune erreur</div>
          ) : (
            issues.map((issue, index) => (
              <div
                key={index}
                className={`whitespace-pre-wrap break-words ${issue.severity === 'error' ? 'text-red-400' : 'text-amber-300'}`}
              >
                {issue.line !== null ? (
                  <button
                    type="button"
                    onClick={() => onIssueClick(issue.line as number)}
                    className="text-left hover:underline decoration-dotted underline-offset-2"
                    title={`Aller à la ligne ${issue.line}`}
                  >
                    {issue.severity === 'error' ? '✗' : '⚠'} {issue.message}
                  </button>
                ) : (
                  <span>{issue.severity === 'error' ? '✗' : '⚠'} {issue.message}</span>
                )}
              </div>
            ))
          )}
          {compiledOk && issues.some((i) => i.severity === 'warning') && (
            <div className="pt-1.5 mt-1.5 border-t border-gray-700 text-gray-400 text-xs">
              Les avertissements n'empêchent pas la simulation.
            </div>
          )}
        </div>
        {errors.length > 0 && (
          <div className="px-3 py-2 border-t border-gray-700 bg-gray-800/60 flex flex-wrap gap-2">
            {errors.map((err) => {
              const key = explainKey(err);
              const isLoading = explanation?.key === key && explanation.status === 'loading';
              const label = err.kind === 'unsupported'
                ? `Demander une explication sur \`${err.symbol ?? '?'}\``
                : err.kind === 'syntax'
                  ? `Demander une explication (ligne ${err.line})`
                  : `Demander une explication (erreur à l'exécution, ligne ${err.line})`;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onExplain(err)}
                  disabled={isLoading}
                  className="inline-flex items-center px-3 py-1 text-xs font-medium text-purple-200 bg-purple-900/40 border border-purple-700 rounded-md hover:bg-purple-900/70 disabled:opacity-60 disabled:cursor-wait transition-colors"
                  title="Le Coach (IA) explique pourquoi ce n'est pas au programme et quoi utiliser à la place"
                >
                  {isLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <HelpCircle className="w-3.5 h-3.5 mr-1" />}
                  {label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {explanation && (
        <div
          className="rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 p-4"
          data-testid="coach-explanation"
        >
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-purple-900 dark:text-purple-200">
              🤖 Explication du coach{explanation.symbol ? ` — ${explanation.symbol}` : ''}
            </h4>
            <span className="text-[11px] text-purple-700 dark:text-purple-300">Réponse IA</span>
          </div>
          {explanation.status === 'loading' && (
            <div className="flex items-center text-sm text-purple-800 dark:text-purple-200">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Le Coach réfléchit…
            </div>
          )}
          {explanation.status === 'error' && (
            <div className="text-sm text-red-700 dark:text-red-300">{explanation.error ?? 'Le Coach est indisponible pour le moment.'}</div>
          )}
          {explanation.status === 'done' && explanation.text && (
            <div className="text-sm text-purple-950 dark:text-purple-100 leading-relaxed">
              <ReactMarkdown
                components={{
                  strong: ({ children }) => <strong className="font-bold">{children}</strong>,
                  code: ({ children }) => <code className="bg-purple-100 dark:bg-purple-900/50 text-purple-900 dark:text-purple-100 px-1 py-0.5 rounded text-xs font-mono">{children}</code>,
                  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                  ul: ({ children }) => <ul className="list-disc pl-5 mb-2">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal pl-5 mb-2">{children}</ol>,
                  li: ({ children }) => <li className="mb-1">{children}</li>,
                }}
              >
                {explanation.text}
              </ReactMarkdown>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SimulatorConsole;
